import { useState, useCallback } from "react";

/**
 * Custom hook for managing cart and assignment states
 * Consolidates cart, return cart, and assignment-related state
 */
export const useCartState = () => {
  // Cart states
  const [cart, setCart] = useState([]);
  const [returnCart, setReturnCart] = useState([]);
  const [editingQuantities, setEditingQuantities] = useState({});
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);

  // Cart handlers
  const addToCart = useCallback((item) => {
    setCart((prev) => [...prev, item]);
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCart((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const updateCartQuantity = useCallback((itemId, newQuantity) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const updateCartNote = useCallback((itemId, note) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, note } : item
      )
    );
  }, []);

  // Return cart handlers
  const addToReturnCart = useCallback((item) => {
    setReturnCart((prev) => [...prev, item]);
  }, []);

  const removeFromReturnCart = useCallback((itemId) => {
    setReturnCart((prev) => prev.filter((item) => item.itemId !== itemId));
  }, []);

  const updateReturnCartQuantity = useCallback((itemId, newQuantity) => {
    setReturnCart((prev) =>
      prev.map((item) =>
        item.itemId === itemId ? { ...item, newQuantity } : item
      )
    );
  }, []);

  const clearReturnCart = useCallback(() => {
    setReturnCart([]);
  }, []);

  // Assignment handlers
  const addPendingAssignment = useCallback((assignment) => {
    setPendingAssignments((prev) => [...prev, assignment]);
  }, []);

  const removePendingAssignment = useCallback((assignmentId) => {
    setPendingAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
  }, []);

  const clearPendingAssignments = useCallback(() => {
    setPendingAssignments([]);
  }, []);

  // Quantity editing handlers
  const startEditingQuantity = useCallback((itemId) => {
    setEditingQuantities((prev) => ({ ...prev, [itemId]: true }));
  }, []);

  const stopEditingQuantity = useCallback((itemId) => {
    setEditingQuantities((prev) => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
  }, []);

  const clearAllEditingQuantities = useCallback(() => {
    setEditingQuantities({});
  }, []);

  // Reset all cart-related state
  const resetAllCarts = useCallback(() => {
    clearCart();
    clearReturnCart();
    clearPendingAssignments();
    clearAllEditingQuantities();
    setSelectedAssignmentId(null);
  }, [
    clearCart,
    clearReturnCart,
    clearPendingAssignments,
    clearAllEditingQuantities,
  ]);

  return {
    // Cart states
    cart,
    setCart,
    returnCart,
    setReturnCart,
    editingQuantities,
    setEditingQuantities,
    pendingAssignments,
    setPendingAssignments,
    selectedAssignmentId,
    setSelectedAssignmentId,

    // Cart handlers
    addToCart,
    removeFromCart,
    updateCartQuantity,
    updateCartNote,
    clearCart,

    // Return cart handlers
    addToReturnCart,
    removeFromReturnCart,
    updateReturnCartQuantity,
    clearReturnCart,

    // Assignment handlers
    addPendingAssignment,
    removePendingAssignment,
    clearPendingAssignments,

    // Quantity editing handlers
    startEditingQuantity,
    stopEditingQuantity,
    clearAllEditingQuantities,

    // Utility handlers
    resetAllCarts,
  };
};
